// shareTeam.js — the people the Share box offers, one link each.
// ---------------------------------------------------------------------------
// v0.281.0 (2026-10-30). Brett: "We have a project team page for droppin. Can
// we use that instead of people typing their name in", then "Couldn't you send
// a unique link to everyone ... You know who is responding." The Share box
// offers the project team page first; on a report share the report's own
// distribution adds anyone not already on it. Each person picked gets their
// own link, so the link says who is replying.
//
// One person per address (any case), first list wins. Someone with no address
// cannot be sent a link: they come back in `missing` by name, so the sender
// sees who is left out rather than losing them silently.
//
// v0.282.0. Brett: "Yes share by group ... I don't want a wall of contacts."
// Each person carries their contact type (Contacts.Kind), so the Share box can
// offer them in groups: Clients, Subcontractors and so on. A report's own
// distribution has a free-text role instead; a role that names a type ("Client",
// "Subcontractors") counts as that type, anything else is Other.

const clean = (v) => String(v == null ? '' : v).trim();

// The order the Share box lists the groups in. The keys are Contacts.Kind's.
export const KIND_GROUPS = [
  { kind: 'client',        label: 'Clients' },
  { kind: 'consultant',    label: 'Consultants' },
  { kind: 'subcontractor', label: 'Subcontractors' },
  { kind: 'supplier',      label: 'Suppliers' },
  { kind: 'builder',       label: 'Builders' },
  { kind: 'staff',         label: 'Staff' },
  { kind: 'other',         label: 'Other' },
];
const KINDS = new Set(KIND_GROUPS.map((g) => g.kind));

// A Kind as stored, or a role that names one ("Client", "Subcontractors"); null if neither.
function kindNamed(v) {
  const k = clean(v).toLowerCase();
  if (KINDS.has(k)) return k;
  if (k.endsWith('s') && KINDS.has(k.slice(0, -1))) return k.slice(0, -1);
  return null;
}

export function mergeTeam(lists) {
  const people = [];
  const seen = new Set();
  const missingRows = [];
  for (const list of lists || []) {
    for (const row of Array.isArray(list) ? list : []) {
      if (!row) continue;
      const email = clean(row.email);
      const name = clean(row.name);
      if (!email) { if (name) missingRows.push(name); continue; }
      const key = email.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      // A role that is only a type is shown by the group, not repeated as a company.
      const roleKind = kindNamed(row.role);
      let kind = kindNamed(row.kind) || roleKind || 'other';
      // v0.314.0 — a chat set for this project wins over the contact's type:
      // they are grouped, and their link made, as that chat's type here.
      const chat = projectChat(row);
      if (chat && kind !== 'staff' && chatForKind(kind) !== chat) kind = chat === 'client' ? 'client' : 'subcontractor';
      people.push({
        key, name: name || email, email,
        company: clean(row.company) || (roleKind ? '' : clean(row.role)) || null,
        kind,
      });
    }
  }
  const named = new Set(people.map((p) => p.name.toLowerCase()));
  const missing = [];
  for (const n of missingRows) {
    const k = n.toLowerCase();
    if (named.has(k)) continue;
    named.add(k);
    missing.push(n);
  }
  return { people, missing };
}

// The people in Share-box order: one entry per type that has anyone, each
// sorted by name.
export function groupTeam(people) {
  const byName = (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  return KIND_GROUPS
    .map((g) => ({ ...g, people: (people || []).filter((p) => p && p.kind === g.kind).sort(byName) }))
    .filter((g) => g.people.length);
}

// Does a person match what was typed in the Share box's search? Every word must
// appear in their name, company or address, in any case.
export function matchesPerson(person, query) {
  const words = clean(query).toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = [person?.name, person?.company, person?.email].map(clean).join(' ').toLowerCase();
  return words.every((w) => hay.includes(w));
}

// v0.283.0. Brett: "Different chat for subbies and clients." Each link is for
// one chat: the client's, the subcontractors', or both (our own staff). A
// person's contact type says which; null = it does not (a builder, Other, a
// typed address), and the Share box asks. Same answers as the server's
// chatForKind (api/shared/chats.js).
export const CHAT_CHOICES = [
  { chat: 'client', label: 'Client chat' },
  { chat: 'trade',  label: 'Subcontractor chat' },
  { chat: 'all',    label: 'Both chats' },
];
export function chatForKind(kind) {
  const k = clean(kind).toLowerCase();
  if (k === 'client' || k === 'consultant') return 'client';
  if (k === 'subcontractor' || k === 'supplier') return 'trade';
  if (k === 'staff') return 'all';
  return null;
}

// v0.314.0. Brett: "i think ecr might be both a client and a subcontractor."
// A contact has one type, but a project team member may be set to the other
// chat on that project only (ProjectContacts.Chat, api/project-contacts).
// projectChat = the override or null; teamChat = the chat they are in here.
export function projectChat(member) {
  const c = clean(member && member.chat).toLowerCase();
  return c === 'client' || c === 'trade' ? c : null;
}
export function teamChat(member) {
  if (chatForKind(member && member.kind) === 'all') return 'all';
  return projectChat(member) || chatForKind(member && member.kind);
}

// v0.332.0. Brett: "do it best in class". Once a project's reports are stamped
// for a client company, a Client link goes only to someone with a SEAT there
// (or our own staff, or the sender), and a Both link only to our own staff (or
// the sender). Anyone else is sent the Subcontractor chat. The same rule as
// the server's (api/shared/client-seat.js decide) — the server refuses whatever
// this box sends; this only shows it before Send. seats = the answer of
// GET /api/report-shares?seatsFor=...: { companies, people: { email: { side } }, you }.
// null = every chat (nothing stamped); otherwise the chats this address may have.
export function seatChats(seats, email) {
  const companies = (seats && Array.isArray(seats.companies)) ? seats.companies : [];
  if (!companies.length) return null;
  const e = clean(email).toLowerCase();
  if (!e) return ['trade'];
  const side = (seats.people && seats.people[e] && seats.people[e].side) || 'none';
  const self = !!seats.you && e === String(seats.you).toLowerCase();
  if (side === 'staff' || self) return ['client', 'trade', 'all'];
  if (side === 'seat') return ['client', 'trade'];
  return ['trade'];
}
// The chat a link is actually made for. A chat the address may not have moves
// to the nearest one it may: Both -> Client for a seat, else Subcontractor.
export function seatChat(chat, allowed) {
  const c = chat || 'trade';
  if (!allowed || allowed.includes(c)) return { chat: c, moved: false };
  if (c === 'all' && allowed.includes('client')) return { chat: 'client', moved: true };
  return { chat: 'trade', moved: true };
}
export function companyNames(seats) {
  const names = ((seats && seats.companies) || []).map((c) => c && c.name).filter(Boolean);
  if (!names.length) return 'the client company';
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
}
